import { useState, useEffect } from "react";
import { fetchUserReports, fetchReportWithUrl, deleteReport, retryExtraction } from "../lib/reportUpload";

const formatDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const statusStyle = (status) => {
  switch (status) {
    case "completed":
      return { bg: "#D1FAE5", color: "#065F46", label: "Analyzed" };
    case "processing":
      return { bg: "#FEF3C7", color: "#92400E", label: "Processing" };
    case "failed":
      return { bg: "#FEE2E2", color: "#991B1B", label: "Failed" };
    default:
      return { bg: "#F3F4F6", color: "#374151", label: status || "Pending" };
  }
};

const HORMONE_LABELS = [
  { key: "extracted_lh", label: "LH", unit: "mIU/mL" },
  { key: "extracted_fsh", label: "FSH", unit: "mIU/mL" },
  { key: "extracted_insulin", label: "Insulin", unit: "uIU/mL" },
  { key: "extracted_testosterone", label: "Testosterone", unit: "ng/dL" },
  { key: "extracted_amh", label: "AMH", unit: "ng/mL" },
];

export default function MyReportsModal({ userId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selectedLoading, setSelectedLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [retryingId, setRetryingId] = useState(null);
  const [error, setError] = useState("");

  const loadReports = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchUserReports(userId);
      setReports(data);
    } catch (e) {
      setError(e.message || "Could not load reports");
    }
    setLoading(false);
  };

  useEffect(() => {
    let cancelled = false;
    if (!userId) return;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const data = await fetchUserReports(userId);
        if (!cancelled) setReports(data);
      } catch (e) {
        if (!cancelled) setError(e.message || "Could not load reports");
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const openReport = async (report) => {
    setSelectedLoading(true);
    setSelected(report); // optimistic
    try {
      const full = await fetchReportWithUrl(report.id);
      if (full) setSelected(full);
    } catch (e) {
      // keep optimistic display
    }
    setSelectedLoading(false);
  };

  const handleDelete = async (report) => {
    if (!window.confirm(`Delete this report (${report.file_name})? This cannot be undone.`)) return;
    setDeletingId(report.id);
    try {
      await deleteReport(report);
      setReports((prev) => prev.filter((r) => r.id !== report.id));
      if (selected?.id === report.id) setSelected(null);
    } catch (e) {
      alert(e.message || "Delete failed");
    }
    setDeletingId(null);
  };

  const handleRetry = async (report) => {
    setRetryingId(report.id);
    try {
      const { extraction, error: extErr } = await retryExtraction(report);
      if (extErr) {
        alert(`Retry failed: ${extErr}`);
      } else {
        // Refresh from DB to get the latest analysis_status + extracted values
        const fresh = await fetchReportWithUrl(report.id);
        if (fresh) {
          setReports((prev) => prev.map((r) => (r.id === report.id ? { ...fresh } : r)));
          if (selected?.id === report.id) setSelected(fresh);
        }
        if (extraction?.status === "failed") {
          alert("Retry completed but extraction still failed. Try a clearer image or enter manually.");
        }
      }
    } catch (e) {
      alert(e.message || "Retry failed");
    }
    setRetryingId(null);
  };

  const handleDownload = (report) => {
    if (!report.signed_url) {
      alert("Please open the report once first so the file link is generated.");
      return;
    }
    const a = document.createElement("a");
    a.href = report.signed_url;
    a.download = report.file_name || "report";
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // ── Modal: Detail view ──
  if (selected) {
    const st = statusStyle(selected.analysis_status);
    const isImage = selected.file_type?.startsWith("image/");
    const isPdf = selected.file_type === "application/pdf";

    return (
      <ModalShell title="Report Details" onClose={() => setSelected(null)} testid="reports-detail-modal">
        <div style={{ padding: 20, overflowY: "auto" }}>
          {/* File preview */}
          {isImage && selected.signed_url && (
            <img
              src={selected.signed_url}
              alt={selected.file_name}
              style={{
                width: "100%", maxHeight: 320, objectFit: "contain",
                borderRadius: 12, background: "#F7F6FB", marginBottom: 16,
              }}
              data-testid="report-image"
            />
          )}
          {isPdf && (
            <div style={{
              padding: 20, background: "#F3F4F6", borderRadius: 12, textAlign: "center", marginBottom: 16,
            }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#6D5EF5" strokeWidth="2" style={{ margin: "0 auto 8px", display: "block" }}>
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>{selected.file_name}</div>
              {selected.signed_url && (
                <a
                  href={selected.signed_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-block", marginTop: 10, padding: "8px 14px",
                    background: "#6D5EF5", color: "#fff", borderRadius: 8,
                    fontSize: 13, fontWeight: 600, textDecoration: "none",
                  }}
                  data-testid="open-pdf-link"
                >
                  Open PDF
                </a>
              )}
            </div>
          )}
          {selectedLoading && !selected.signed_url && (
            <div style={{ padding: 16, textAlign: "center", color: "#8A8A9A", fontSize: 13 }}>
              Loading file…
            </div>
          )}

          {/* Meta */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 11, color: "#8A8A9A", textTransform: "uppercase", letterSpacing: "0.06em" }}>Uploaded</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#2F2F35" }}>{formatDate(selected.created_at)}</div>
            </div>
            <span style={{
              background: st.bg, color: st.color, padding: "4px 10px",
              borderRadius: 10, fontSize: 11, fontWeight: 600,
            }}>
              {st.label}
            </span>
          </div>

          {/* Extracted hormones */}
          <div style={{ marginTop: 16 }}>
            <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10, color: "#2F2F35" }}>Extracted Values</h4>
            <div style={{ background: "#F9FAFB", borderRadius: 12, padding: 12 }}>
              {HORMONE_LABELS.map((h) => {
                const v = selected[h.key];
                return (
                  <div key={h.key} style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "6px 0", borderBottom: "1px solid #EEEAF8",
                  }}>
                    <span style={{ fontSize: 13, color: "#374151" }}>{h.label}</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: v != null ? "#6D5EF5" : "#9CA3AF" }}>
                      {v != null ? `${v} ${h.unit}` : "Not detected"}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Analysis summary */}
          {selected.analysis_summary && (
            <div style={{ marginTop: 16 }}>
              <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 6, color: "#2F2F35" }}>Analysis</h4>
              <p style={{ fontSize: 13, color: "#374151", lineHeight: 1.6, margin: 0 }}>
                {selected.analysis_summary}
              </p>
            </div>
          )}

          {selected.error_message && (
            <div style={{
              marginTop: 12, background: "#FEE2E2", color: "#991B1B",
              padding: "10px 12px", borderRadius: 8, fontSize: 12,
            }}>
              {selected.error_message}
            </div>
          )}

          {/* Action buttons */}
          <div style={{ display: "flex", gap: 8, marginTop: 20, flexWrap: "wrap" }}>
            <button
              onClick={() => handleDownload(selected)}
              disabled={!selected.signed_url}
              style={{
                flex: 1, minWidth: 120, padding: 10,
                background: "#EDE9FE", color: "#6D5EF5", border: "none",
                borderRadius: 10, fontWeight: 600, fontSize: 13,
                cursor: selected.signed_url ? "pointer" : "not-allowed",
                opacity: selected.signed_url ? 1 : 0.6,
              }}
              data-testid="download-report-btn"
            >
              ↓ Download
            </button>
            {["failed", "processing"].includes(selected.analysis_status) && (
              <button
                onClick={() => handleRetry(selected)}
                disabled={retryingId === selected.id}
                style={{
                  flex: 1, minWidth: 120, padding: 10,
                  background: "#FEF3C7", color: "#92400E", border: "none",
                  borderRadius: 10, fontWeight: 600, fontSize: 13, cursor: "pointer",
                }}
                data-testid="retry-report-btn"
              >
                {retryingId === selected.id ? "Retrying…" : "↻ Retry Analysis"}
              </button>
            )}
            <button
              onClick={() => handleDelete(selected)}
              disabled={deletingId === selected.id}
              style={{
                flex: 1, minWidth: 120, padding: 10,
                background: "#FEE2E2", color: "#991B1B", border: "none",
                borderRadius: 10, fontWeight: 600, fontSize: 13, cursor: "pointer",
              }}
              data-testid="delete-report-btn"
            >
              {deletingId === selected.id ? "Deleting…" : "Delete"}
            </button>
          </div>
        </div>
      </ModalShell>
    );
  }

  // ── Modal: List view ──
  return (
    <ModalShell title="My Reports" onClose={onClose} testid="my-reports-modal">
      <div style={{ padding: 20, overflowY: "auto", maxHeight: "calc(80vh - 60px)" }}>
        {loading && (
          <div style={{ padding: 24, textAlign: "center", color: "#8A8A9A", fontSize: 13 }}>
            Loading reports…
          </div>
        )}
        {!loading && error && (
          <div style={{
            background: "#FEE2E2", color: "#991B1B", padding: 12, borderRadius: 8, fontSize: 13,
          }}>
            {error}
          </div>
        )}
        {!loading && !error && reports.length === 0 && (
          <div style={{ padding: 24, textAlign: "center" }} data-testid="reports-empty-state">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#A78BFA" strokeWidth="1.5" style={{ margin: "0 auto 12px", display: "block" }}>
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#2F2F35", marginBottom: 4 }}>
              No reports yet
            </div>
            <div style={{ fontSize: 12, color: "#8A8A9A" }}>
              Upload a blood report from the Hormones tab to see it here.
            </div>
          </div>
        )}
        {!loading && reports.map((r) => {
          const st = statusStyle(r.analysis_status);
          return (
            <div
              key={r.id}
              style={{
                display: "flex", alignItems: "center", gap: 12, padding: 12,
                background: "#fff", border: "1px solid #EEEAF8", borderRadius: 12,
                marginBottom: 10, cursor: "pointer",
              }}
              onClick={() => openReport(r)}
              data-testid={`report-row-${r.id}`}
            >
              {/* Thumbnail */}
              <div style={{
                width: 48, height: 48, borderRadius: 8,
                background: "linear-gradient(135deg, #EDE9FE, #DDD6FE)",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                {r.file_type?.startsWith("image/") ? (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#6D5EF5" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                ) : (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#6D5EF5" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                )}
              </div>
              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 13, fontWeight: 600, color: "#2F2F35",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {r.file_name}
                </div>
                <div style={{ fontSize: 11, color: "#8A8A9A", marginTop: 2 }}>
                  {formatDate(r.created_at)}
                </div>
                <span style={{
                  display: "inline-block", marginTop: 4,
                  background: st.bg, color: st.color, padding: "2px 8px",
                  borderRadius: 8, fontSize: 10, fontWeight: 600,
                }}>
                  {st.label}
                </span>
              </div>
              {/* Actions */}
              <div style={{ display: "flex", gap: 6 }} onClick={(e) => e.stopPropagation()}>
                {r.analysis_status === "failed" && (
                  <button
                    onClick={() => handleRetry(r)}
                    disabled={retryingId === r.id}
                    style={{
                      background: "#FEF3C7", color: "#92400E", border: "none",
                      borderRadius: 8, padding: "6px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer",
                    }}
                    data-testid={`retry-row-${r.id}`}
                  >
                    {retryingId === r.id ? "…" : "Retry"}
                  </button>
                )}
                <button
                  onClick={() => openReport(r)}
                  style={{
                    background: "#EDE9FE", color: "#6D5EF5", border: "none",
                    borderRadius: 8, padding: "6px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer",
                  }}
                  data-testid={`view-report-${r.id}`}
                >
                  View
                </button>
                <button
                  onClick={() => handleDelete(r)}
                  disabled={deletingId === r.id}
                  style={{
                    background: "#FEE2E2", color: "#991B1B", border: "none",
                    borderRadius: 8, padding: "6px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer",
                  }}
                  data-testid={`delete-report-${r.id}`}
                >
                  {deletingId === r.id ? "…" : "Delete"}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </ModalShell>
  );
}

// Lightweight modal shell — matches existing ProfileScreen modal styling
function ModalShell({ title, onClose, testid, children }) {
  return (
    <div
      style={{
        position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
        background: "rgba(0,0,0,0.5)", display: "flex",
        alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20,
      }}
      onClick={onClose}
      data-testid={testid}
    >
      <div
        style={{
          background: "#fff", borderRadius: 20, maxWidth: 440, width: "100%",
          maxHeight: "85vh", display: "flex", flexDirection: "column",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)", overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          padding: "16px 20px", display: "flex", justifyContent: "space-between",
          alignItems: "center", background: "linear-gradient(135deg, #A78BFA, #6D5EF5)",
        }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: "#fff", margin: 0 }}>{title}</h3>
          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.2)", border: "none", borderRadius: 8,
              width: 32, height: 32, display: "flex", alignItems: "center",
              justifyContent: "center", cursor: "pointer", color: "#fff", fontSize: 18,
            }}
            data-testid="close-reports-modal-btn"
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
